import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { Product, GiftCode, Coupon } from '../../../types';
import { getProducts } from '../../../services/membershipApi/products';
import { usePaymentContext } from './PaymentContext';
import { useMetrics } from '../../../hooks/useMetrics';
import { normalizeGiftCodeLength } from '../../../utils/giftCodeHelpers';

interface ProductContextType {
    // Price-based selection
    selectedPriceId: string | null;
    selectedProduct: Product | null; // Derived from selectedPriceId
    selectedBillingCycle: 'monthly' | 'annual' | null; // Derived from selectedPriceId
    availableProducts: Product[];
    isLoadingProducts: boolean;

    // Direct priceId selection (preferred method)
    selectPriceById: (priceId: string) => void;

    // Convenience method for product + billing cycle
    selectPrice: (product: Product, billingCycle: 'monthly' | 'annual') => void;
    clearSelection: () => void;

    // Helper to check if a specific price is selected
    isSelected: (product: Product, billingCycle: 'monthly' | 'annual') => boolean;
    isSelectedById: (priceId: string) => boolean;

    // Gift code functionality
    appliedGiftCode: GiftCode | null;
    isGiftCodeApplied: boolean;
    applyGiftCode: (giftCode: GiftCode) => void;
    removeGiftCode: () => void;

    // Coupon functionality
    appliedCoupon: Coupon | null;
    isCouponApplied: boolean;
    applyCoupon: (coupon: Coupon) => void;
    removeCoupon: () => void;
    getAvailableCoupons: () => Coupon[];

    // Marketing promo code
    appliedMarketingPromo: { code: string } | null;
    marketingPromoError: string | null;
    marketingPromoErrorCode: string | null;
    applyMarketingPromo: (code: string) => void;
    removeMarketingPromo: () => void;
    setMarketingPromoError: (error: string | null, errorCode: string | null) => void;
    clearMarketingPromoError: () => void;

    // Error handling
    error: string | null;
    clearError: () => void;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
    const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);
    const [appliedGiftCode, setAppliedGiftCode] = useState<GiftCode | null>(null);
    const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
    const [appliedMarketingPromo, setAppliedMarketingPromo] = useState<{ code: string } | null>(null);
    const [marketingPromoError, setMarketingPromoError] = useState<string | null>(null);
    const [marketingPromoErrorCode, setMarketingPromoErrorCode] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Get payment context to set billing cycle based on gift code duration
    const { setBillingCycle } = usePaymentContext();
    const { recordProductSelected } = useMetrics();

    // Derive selectedProduct from selectedPriceId
    const selectedProduct = useMemo(() => {
        if (!selectedPriceId || !availableProducts.length) return null;

        return availableProducts.find(product =>
            product.pricing.monthly?.id === selectedPriceId ||
            product.pricing.annual?.id === selectedPriceId
        ) || null;
    }, [selectedPriceId, availableProducts]);

    // Derive selectedBillingCycle from selectedPriceId
    const selectedBillingCycle = useMemo(() => {
        if (!selectedPriceId || !selectedProduct) return null;

        if (selectedProduct.pricing.monthly?.id === selectedPriceId) return 'monthly';
        if (selectedProduct.pricing.annual?.id === selectedPriceId) return 'annual';
        return null;
    }, [selectedPriceId, selectedProduct]);

    // Load products on mount
    useEffect(() => {
        const loadProducts = async () => {
            try {
                setIsLoadingProducts(true);
                const products = await getProducts();
                setAvailableProducts(products);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load membership products');
            } finally {
                setIsLoadingProducts(false);
            }
        };

        loadProducts();
    }, []);

    // Direct priceId selection (preferred method)
    const selectPriceById = useCallback((priceId: string) => {
        setSelectedPriceId(priceId);
        setError(null);

        // Auto-apply the first active coupon for the selected price
        const product = availableProducts.find(p =>
            p.pricing.monthly?.id === priceId ||
            p.pricing.annual?.id === priceId
        );

        if (product) {
            const billingCycle = product.pricing.monthly?.id === priceId ? 'monthly' : 'annual';
            const pricing = billingCycle === 'monthly' ? product.pricing.monthly : product.pricing.annual;
            const activeCoupon = pricing?.coupons?.find(coupon => coupon.isActive);
            
            // Record product selection
            recordProductSelected(
                product.tierId || product.id || '',
                product.title || product.name || '',
                billingCycle,
                priceId,
                {
                    product_id: product.tierId || product.id || '',
                    product_name: product.title || product.name || '',
                }
            );

            if (activeCoupon) {
                setAppliedCoupon(activeCoupon);
            } else {
                // Clear any previously applied coupon if no active coupon for this price
                setAppliedCoupon(null);
            }
        }
    }, [availableProducts, recordProductSelected]);

    // Convenience method for product + billing cycle
    const selectPrice = useCallback((product: Product, billingCycle: 'monthly' | 'annual') => {
        const priceId = billingCycle === 'monthly'
            ? product.pricing.monthly?.id
            : product.pricing.annual?.id;

        if (priceId) {
            selectPriceById(priceId); // This will trigger auto-coupon application
        } else {
            setError(`No ${billingCycle} pricing found for ${product.name}`);
        }
    }, [selectPriceById]);

    const clearSelection = useCallback(() => {
        setSelectedPriceId(null);
        setAppliedCoupon(null); // Clear coupon when clearing selection
        setError(null);
    }, []);

    const isSelectedById = useCallback((priceId: string) => {
        return selectedPriceId === priceId;
    }, [selectedPriceId]);

    const isSelected = useCallback((product: Product, billingCycle: 'monthly' | 'annual') => {
        const priceId = billingCycle === 'monthly'
            ? product.pricing.monthly?.id
            : product.pricing.annual?.id;

        return priceId ? isSelectedById(priceId) : false;
    }, [isSelectedById]);

    const applyGiftCode = useCallback((giftCode: GiftCode) => {
        setAppliedGiftCode(giftCode);

        // Find and select the product associated with the gift code
        const product = availableProducts.find(p => p.tierId === giftCode.tierId);
        if (product) {
            // Normalize length: 13 months -> 12 months (matches backend logic)
            const normalizedLength = normalizeGiftCodeLength(giftCode.length);

            // Determine billing cycle from normalized duration (matches backend logic)
            const isAnnual = normalizedLength === 12;
            const billingCycle = isAnnual ? 'annual' : 'monthly';

            // Select the appropriate price based on billing cycle
            const priceId = isAnnual
                ? product.pricing.annual?.id
                : product.pricing.monthly?.id;

            if (priceId) {
                setSelectedPriceId(priceId);
                setBillingCycle(billingCycle);
                setError(null); // Clear any previous errors on successful selection
            } else {
                // Fallback: try the other billing cycle if preferred one doesn't exist
                // WARNING: This is an edge case that should rarely occur. If a 12-month gift
                // falls back to monthly pricing, the user will be charged monthly after the
                // gift expires instead of annually, which could be confusing.
                const fallbackBillingCycle = isAnnual ? 'monthly' : 'annual';
                const fallbackPriceId = isAnnual
                    ? product.pricing.monthly?.id
                    : product.pricing.annual?.id;

                if (fallbackPriceId) {
                    console.warn(
                        `[ProductContext] Gift code billing cycle fallback: ` +
                        `Expected ${billingCycle} pricing for ${normalizedLength}-month gift code ` +
                        `but falling back to ${fallbackBillingCycle} pricing. ` +
                        `Product: ${product.name}, Tier: ${product.tierId}`,
                        {
                            giftCodeLength: giftCode.length,
                            normalizedLength,
                            expectedBillingCycle: billingCycle,
                            fallbackBillingCycle,
                            productId: product.id,
                            tierId: product.tierId,
                        }
                    );

                    setSelectedPriceId(fallbackPriceId);
                    setBillingCycle(fallbackBillingCycle);

                    // Set error to inform user about the unexpected billing cycle
                    // Note: We intentionally keep this error visible to the user
                    setError(
                        `Note: This ${normalizedLength}-month gift code will use ${fallbackBillingCycle} billing. ` +
                        `After the gift expires, your subscription will renew ${fallbackBillingCycle === 'monthly' ? 'monthly' : 'annually'}.`
                    );
                } else {
                    // No pricing available at all - this should be caught by backend validation
                    setError(
                        `No pricing available for ${product.name}. Please contact support.`
                    );
                }
            }
        } else {
            // Product not found for this gift code tier
            setError(`Product not found for gift code tier: ${giftCode.tierId}`);
        }
    }, [availableProducts, setBillingCycle]);

    const removeGiftCode = useCallback(() => {
        setAppliedGiftCode(null);
        // Note: We don't clear selectedProduct here as user might want to keep the selection
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Coupon functionality
    const applyCoupon = useCallback((coupon: Coupon) => {
        setAppliedCoupon(coupon);
        setError(null);
    }, []);

    const removeCoupon = useCallback(() => {
        setAppliedCoupon(null);
    }, []);

    const getAvailableCoupons = useCallback((): Coupon[] => {
        if (!selectedProduct || !selectedBillingCycle) return [];

        const pricing = selectedBillingCycle === 'monthly'
            ? selectedProduct.pricing.monthly
            : selectedProduct.pricing.annual;

        return pricing?.coupons?.filter(coupon => coupon.isActive) || [];
    }, [selectedProduct, selectedBillingCycle]);

    // Marketing promo code
    const applyMarketingPromo = useCallback((code: string) => {
        setAppliedMarketingPromo({ code });
        setMarketingPromoError(null);
        setMarketingPromoErrorCode(null);
    }, []);

    const removeMarketingPromo = useCallback(() => {
        setAppliedMarketingPromo(null);
        setMarketingPromoError(null);
        setMarketingPromoErrorCode(null);
    }, []);

    const setMarketingPromoErrorFn = useCallback((error: string | null, errorCode: string | null) => {
        setMarketingPromoError(error);
        setMarketingPromoErrorCode(errorCode);
    }, []);

    const clearMarketingPromoError = useCallback(() => {
        setMarketingPromoError(null);
        setMarketingPromoErrorCode(null);
    }, []);

    const isGiftCodeApplied = Boolean(appliedGiftCode);
    const isCouponApplied = Boolean(appliedCoupon);

    const value: ProductContextType = {
        selectedPriceId,
        selectedProduct,
        selectedBillingCycle,
        availableProducts,
        isLoadingProducts,
        selectPriceById,
        selectPrice,
        clearSelection,
        isSelected,
        isSelectedById,
        appliedGiftCode,
        isGiftCodeApplied,
        applyGiftCode,
        removeGiftCode,
        appliedCoupon,
        isCouponApplied,
        applyCoupon,
        removeCoupon,
        getAvailableCoupons,
        appliedMarketingPromo,
        marketingPromoError,
        marketingPromoErrorCode,
        applyMarketingPromo,
        removeMarketingPromo,
        setMarketingPromoError: setMarketingPromoErrorFn,
        clearMarketingPromoError,
        error,
        clearError,
    };

    return (
        <ProductContext.Provider value={value}>
            {children}
        </ProductContext.Provider>
    );
};

export const useProductContext = (): ProductContextType => {
    const context = useContext(ProductContext);
    if (!context) {
        throw new Error('useProductContext must be used within a ProductProvider');
    }
    return context;
};
